import { createConnectTransport } from "@connectrpc/connect-web";
import { createClient, type Client, type Transport } from "@connectrpc/connect";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

import { AdminService } from "@/gen/wabms/admin/v1/admin_pb";
import { isCanceled, isUnauthenticated } from "@/lib/errors";
import { useUiStore } from "@/stores/ui-store";

/**
 * The admin API is served by the same Go binary that serves this SPA, so the
 * transport talks to the current origin. In development Vite proxies the two
 * Connect service prefixes to the backend (see vite.config.ts).
 */
export const transport: Transport = createConnectTransport({
  baseUrl: "/",
  // JSON on the wire. It keeps the network tab readable for an operator tool
  // and costs nothing at this traffic volume.
  useBinaryFormat: false,
  // The admin session is a cookie set by AdminService.Login, so every call has
  // to carry it. `same-origin` would be enough today, but `include` keeps the
  // SPA working when it is served from a different host than the API.
  fetch: (input, init) => globalThis.fetch(input, { ...init, credentials: "include" }),
  defaultTimeoutMs: 30_000,
});

/**
 * Promise client for the calls that do not fit react-query, i.e. the
 * PairAccount server stream. Unary calls go through connect-query instead.
 */
export const adminClient: Client<typeof AdminService> = createClient(AdminService, transport);

/**
 * The session cookie can expire or be revoked from the Sessions screen at any
 * time. Rather than teach every call site about it, a single cache-level
 * handler flips the store flag; `RequireSession` in app.tsx then bounces to
 * /login on the next render. The server remains the authority — this only
 * keeps the SPA from rendering a shell full of failed reads.
 */
function handleAuthFailure(error: unknown): void {
  if (isCanceled(error) || !isUnauthenticated(error)) return;
  const { authenticated, setAuthenticated } = useUiStore.getState();
  if (authenticated) setAuthenticated(false);
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({ onError: handleAuthFailure }),
    mutationCache: new MutationCache({ onError: handleAuthFailure }),
    defaultOptions: {
      queries: {
        // Admin data is cheap to refetch and stale data is misleading when you
        // are looking at a live queue.
        staleTime: 5_000,
        refetchOnWindowFocus: true,
        // Retrying an unauthenticated call only delays the redirect.
        retry: (failureCount, error) => !isUnauthenticated(error) && failureCount < 1,
      },
      mutations: {
        retry: false,
      },
    },
  });
}

export const queryClient = createQueryClient();
